import React from 'react'
import cx from 'classnames'

import ClientApi from './../../api/clientApi'
import { EPathSegment, getLocalStorageItem } from '../../utils'
import MobileNavigation from './components/MobileNavigation'
import CheckoutHeader from './components/CheckoutHeader'
import CountrySelector from '../../components/CountrySelector'
import TickerBar from './components/TickerBar'
import UserBar from './components/UserBar'
import NavigationBar from './components/NavigationBar'
import CustomStyles from './components/CustomStyles'
import AppContext, { TAppContext } from '../../components/AppContext/AppContext'
import Icon, { EIconId } from '../../components/icons'

import { ELocalStorageKeys, ELoginState, ITEM_COUNT_HEADER } from '../../utils/consts'

import { TNavItem } from '../../types/mainNavigation'
import { TTickerItem } from '../../types/tickers'

const Header: React.FC<{
  navItems: TNavItem[]
  tickers: TTickerItem[]
}> = (props) => {
  const [menuClosed, setMenuClosed] = React.useState(true)
  const [checkout, setCheckout] = React.useState(false)
  const [cartCount, setCartCount] = React.useState(getInitialCartCount())
  const [loginState, setLoginState] = React.useState<ELoginState>(ELoginState.NOT_LOGGED_IN)
  const { locale } = React.useContext<TAppContext>(AppContext)

  const updateCartCount = async () => {
    try {
      const identityId = getLocalStorageItem(ELocalStorageKeys.IDENTITY_ID)
      if (!identityId) return // early exit when there is no cognito identity

      let response = await ClientApi.getAuthToken(identityId)
      if (!response.data.Token) return // early exit when there is no cognito auth token

      response = await ClientApi.getBasketItemCount(`Bearer ${response.data.Token}`)
      const count = getItemCount(response.headers)
      setCartCount(count)
    } catch (e) {
      setCartCount(0)
    }
  }

  const updateLoginState = async () => {
    try {
      const response = await ClientApi.getLoginState()
      setLoginState(response.data.state ?? ELoginState.NOT_LOGGED_IN)
    } catch (e) {
      setLoginState(ELoginState.NOT_LOGGED_IN)
    }
  }

  React.useEffect(() => {
    window.eev?.on('show-checkout-header', () => {
      setCheckout(true)
    })
    window.eev?.on('hide-checkout-header', () => {
      setCheckout(false)
    })
    window.eev?.on('update-cart-count', () => {
      updateCartCount()
    })
    window.eev?.on('update-login-state', () => {
      updateLoginState()
    })
    updateCartCount()
    updateLoginState()
  }, [])

  // we add overflow-hidden to preventing the scroll freeze on iphones: https://stackoverflow.com/a/60372628/4166508
  React.useEffect(() => {
    const classesForBody = ['fixed', 'w-full', 'overflow-hidden'] // we do this as we still support IE11
    if (!menuClosed) {
      classesForBody.forEach((c) => document.body.classList.add(c))
    } else {
      classesForBody.forEach((c) => document.body.classList.remove(c))
    }
  }, [menuClosed])

  if (checkout) {
    return (
      <>
        <CustomStyles />
        <CheckoutHeader />
      </>
    )
  }

  return (
    <>
      <CustomStyles />

      {!menuClosed && (
        <div
          className="fixed top-0 z-20 w-full h-full bg-dark opacity-50 lg:hidden"
          onClick={() => setMenuClosed(true)}
        />
      )}
      <header className="relative sm:border-b-2 border-lightgrey-light bg-white">
        {props.tickers.length > 0 && <TickerBar tickers={props.tickers} />}
        <UserBar
          onMenuClick={() => setMenuClosed(!menuClosed)}
          isLoggedIn={loginState === ELoginState.LOGGED_IN}
          cartCount={cartCount}
        />
        <NavigationBar navItems={props.navItems} />
        {props.navItems.length > 0 && <MobileNavigation navItems={props.navItems} menuClosed={menuClosed} />}
      </header>
      <CountrySelector countryCodeUrl={EPathSegment.COUNTRY_CODE} locale={locale} />
      <button
        className={cx('absolute top-0 mt-8 z-20 text-white lg:hidden', {
          hidden: menuClosed,
        })}
        style={{ left: '270px' }}
        onClick={() => setMenuClosed(true)}
      >
        <Icon id={EIconId.CLOSE_EXPERIMENT} className="w-32 lg:hidden" />
      </button>
    </>
  )
}

const getInitialCartCount = (): number => {
  const headerCartCount = Number(getLocalStorageItem(ELocalStorageKeys.HEADER_CART_COUNT)) || 0
  return BUILD_FLAG_IS_SERVER ? 0 : headerCartCount
}

export const getItemCount = (headers: { [key: string]: string }): number => {
  const basketHeaderKey: string =
    Object.keys(headers).find((headerKey) => headerKey.toLocaleLowerCase() === ITEM_COUNT_HEADER.toLowerCase()) ?? ''
  return +headers[basketHeaderKey] || 0
}

export default Header
